import React, { useId, useState } from "react";
import {
  Alert,
  alpha,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { DialogShell } from "../../../shared/ui/DialogShell";

export type ReportMode = "narrative" | "full";

interface ReportModeDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (mode: ReportMode) => void;
  /** Disables both choices and spins the picked one while the resume is in flight. */
  submitting?: boolean;
  /** A rejected approval — shown here because nothing was submitted and the
   *  officer's next move is to pick again. */
  error?: string;
}

interface ModeOption {
  mode: ReportMode;
  title: string;
  summary: string;
  bullets: string[];
  icon: React.ReactNode;
}

// Left = the current default path (narrative only), right = the expanded one.
// Order is meaningful: the officer scans left-to-right from "what happens if I
// do nothing different" to "what the extra step buys".
const OPTIONS: ModeOption[] = [
  {
    mode: "narrative",
    title: "Narrative Only",
    summary: "Just the written narrative, ready to review and sign.",
    bullets: [
      "Fastest to produce",
      "No structured fields to check",
      "Can be expanded to a full report later",
    ],
    icon: <DescriptionOutlinedIcon />,
  },
  {
    mode: "full",
    title: "Full Report",
    summary: "The narrative plus every incident specific extracted for you.",
    bullets: [
      "People, vehicles, locations, and charges",
      "Structured fields filled from the evidence",
      "Takes longer to generate",
      "Recommended for RMS entry"
    ],
    icon: <ArticleOutlinedIcon />,
  },
];

/**
 * Second step of approval: the officer approves the First Draft, then picks
 * what the AI Assistant builds from it. Two large panels — picking one IS the
 * confirmation, there is no separate submit button.
 */
export const ReportModeDialog: React.FC<ReportModeDialogProps> = ({
  open,
  onClose,
  onSelect,
  submitting = false,
  error,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  // Which panel the officer clicked — drives the spinner placement so the
  // feedback lands on the choice they made, not on the dialog as a whole.
  const [pendingMode, setPendingMode] = useState<ReportMode | null>(null);

  const handleSelect = (mode: ReportMode) => {
    if (submitting) return;
    setPendingMode(mode);
    onSelect(mode);
  };

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      closeDisabled={submitting}
      maxWidth={760}
      titleId={titleId}
      descriptionId={descriptionId}
    >
      <DialogContent sx={{ pt: 4, pb: 2, px: { xs: 3, md: 4 } }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            id={titleId}
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            What should we generate?
          </Typography>
          <Typography
            id={descriptionId}
            variant="body1"
            sx={{ color: "text.secondary", lineHeight: 1.6 }}
          >
            Choosing an option submits your approval of the First Draft and
            starts generation.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          {OPTIONS.map((option) => {
            const isPending = submitting && pendingMode === option.mode;

            return (
              <ButtonBase
                key={option.mode}
                onClick={() => handleSelect(option.mode)}
                disabled={submitting}
                aria-label={`${option.title} — ${option.summary}`}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "block",
                  textAlign: "left",
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  transition: (theme) =>
                    theme.transitions.create(
                      ["border-color", "box-shadow", "transform"],
                      { duration: 150 }
                    ),
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: (theme) =>
                      `0 8px 24px ${alpha(theme.palette.primary.main, 0.18)}`,
                    transform: "translateY(-2px)",
                  },
                  "&.Mui-focusVisible": {
                    borderColor: "primary.main",
                    outline: (theme) =>
                      `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                    outlineOffset: 2,
                  },
                  "&.Mui-disabled": { opacity: 0.6 },
                }}
              >
                <Stack spacing={1.5} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.12),
                      color: "primary.main",
                      "& svg": { fontSize: 26 },
                    }}
                  >
                    {isPending ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      option.icon
                    )}
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.summary}
                  </Typography>

                  <Stack spacing={0.75} sx={{ pt: 0.5 }}>
                    {option.bullets.map((bullet) => (
                      <Stack
                        key={bullet}
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                      >
                        <CheckRoundedIcon
                          sx={{ fontSize: 16, mt: "3px", color: "primary.main" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {bullet}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              </ButtonBase>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 3, md: 4 }, pb: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          color="inherit"
          variant="text"
          fullWidth
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            color: "text.secondary",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          Back
        </Button>
      </DialogActions>
    </DialogShell>
  );
};
